import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { SideModal } from './SideModal'
import { FiLoader, FiShoppingCart, FiPlus, FiTrash2, FiChevronDown, FiChevronRight } from 'react-icons/fi'

// Nova venda (PRD §5.5): fluxo comercial que gera N contas a receber vinculadas.

interface Opt { id: number; name: string }
interface Item { description: string; quantity: string; unit_price: string }
type SaleType = 'orcamento' | 'avulsa' | 'recorrente'

// Sem a largura: `${inputCls} w-20` NÃO funciona, porque no CSS gerado do
// Tailwind `.w-full` vence `.w-20` (a ordem no atributo class não decide).
const inputBase = 'neu-convex rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40'
const inputCls = `w-full ${inputBase}`
const brl = (n: number) => n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
const METHODS = [
  { v: 'pix', l: 'Pix' }, { v: 'boleto', l: 'Boleto' }, { v: 'cartao', l: 'Cartão' },
  { v: 'dinheiro', l: 'Dinheiro' }, { v: 'transferencia', l: 'Transferência' }, { v: 'outro', l: 'Outro' },
]
const TYPE_TABS: Array<{ v: SaleType; l: string }> = [
  { v: 'orcamento', l: 'Orçamento' }, { v: 'avulsa', l: 'Venda avulsa' }, { v: 'recorrente', l: 'Recorrente' },
]

export function NovaVenda({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const { authenticatedFetch } = useAuth()
  const today = new Date().toISOString().slice(0, 10)

  const [cats, setCats] = useState<Opt[]>([])
  const [ccs, setCcs] = useState<Opt[]>([])
  const [accs, setAccs] = useState<Opt[]>([])

  const [saleType, setSaleType] = useState<SaleType>('avulsa')
  const [saleNumber, setSaleNumber] = useState('')
  const [client, setClient] = useState('')
  const [clientDoc, setClientDoc] = useState('')
  const [saleDate, setSaleDate] = useState(today)
  const [negotiation, setNegotiation] = useState('')
  const [categoryId, setCategoryId] = useState('')
  const [costCenterId, setCostCenterId] = useState('')
  const [seller, setSeller] = useState('')

  const [detailItems, setDetailItems] = useState(false)
  const [items, setItems] = useState<Item[]>([{ description: '', quantity: '1', unit_price: '' }])
  const [grossTotal, setGrossTotal] = useState('')

  const [discountType, setDiscountType] = useState<'valor' | 'percent'>('valor')
  const [discountValue, setDiscountValue] = useState('')

  const [method, setMethod] = useState('')
  const [accountId, setAccountId] = useState('')
  const [installments, setInstallments] = useState(1)
  const [due, setDue] = useState(today)

  const [fiscalOpen, setFiscalOpen] = useState(false)
  const [fiscalNature, setFiscalNature] = useState('')
  const [notes, setNotes] = useState('')

  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  useEffect(() => {
    (async () => {
      const [rc, rcc, ra] = await Promise.all([
        authenticatedFetch('/api/categories?kind=receita'),
        authenticatedFetch('/api/cost-centers?status=active&pageSize=100'),
        authenticatedFetch('/api/bank-accounts'),
      ])
      const [dc, dcc, da] = await Promise.all([rc.json(), rcc.json(), ra.json()])
      if (dc.success) setCats(dc.categories.map((c: any) => ({ id: c.id, name: c.name })))
      if (dcc.success) setCcs(dcc.items.map((c: any) => ({ id: c.id, name: c.name })))
      if (da.success) setAccs(da.items.map((a: any) => ({ id: a.id, name: a.name })))
    })().catch(() => {})
  }, [authenticatedFetch])

  // cálculo do total (reativo)
  const itemsTotal = detailItems
    ? items.reduce((s, it) => s + (Number(it.quantity) || 0) * (Number(it.unit_price) || 0), 0)
    : Number(grossTotal) || 0
  const discountAmount = discountType === 'percent'
    ? itemsTotal * ((Number(discountValue) || 0) / 100)
    : (Number(discountValue) || 0)
  const netTotal = Math.max(0, itemsTotal - discountAmount)
  const perInstallment = installments > 1 ? netTotal / installments : netTotal

  const setItem = (i: number, k: keyof Item, v: string) =>
    setItems(prev => prev.map((it, idx) => idx === i ? { ...it, [k]: v } : it))
  const addItem = () => setItems(prev => [...prev, { description: '', quantity: '1', unit_price: '' }])
  const removeItem = (i: number) => setItems(prev => prev.filter((_, idx) => idx !== i))

  const save = async () => {
    if (!saleNumber.trim()) { setErr('Informe o número da venda.'); return }
    if (!client.trim()) { setErr('Informe o cliente.'); return }
    if (!categoryId) { setErr('Selecione a categoria de receita.'); return }
    if (!(netTotal > 0)) { setErr('O total líquido deve ser maior que zero.'); return }
    setSaving(true); setErr(null)
    const body: any = {
      sale_number: saleNumber, sale_type: saleType, negotiation_status: negotiation || null,
      client_name: client, client_doc: clientDoc || null, sale_date: saleDate,
      category_id: Number(categoryId), cost_center_id: costCenterId ? Number(costCenterId) : null,
      seller_name: seller || null, discount_type: discountType, discount_value: Number(discountValue) || 0,
      payment_method: method || null, receiving_account_id: accountId ? Number(accountId) : null,
      installments, due_date: due, fiscal_nature: fiscalNature || null, notes: notes || null,
    }
    if (detailItems) {
      body.items = items.filter(it => it.description.trim())
        .map(it => ({ description: it.description, quantity: Number(it.quantity) || 1, unit_price: Number(it.unit_price) || 0 }))
    } else {
      body.gross_total = Number(grossTotal) || 0
    }
    try {
      const r = await authenticatedFetch('/api/sales', { method: 'POST', body: JSON.stringify(body) })
      const d = await r.json()
      if (!d.success) { setErr(d.error || 'Falha ao registrar venda'); return }
      onSaved()
    } catch { setErr('Erro de conexão') }
    finally { setSaving(false) }
  }

  return (
    <SideModal
      title="Nova venda"
      subtitle="Gera as contas a receber automaticamente"
      icon={FiShoppingCart}
      maxWidth="max-w-xl"
      onClose={onClose}
      footer={
        <>
          <div className="mr-auto text-sm">
            <span className="text-muted-foreground">Total líquido: </span>
            <span className="font-semibold text-foreground">{brl(netTotal)}</span>
            {installments > 1 && <span className="text-[13px] text-muted-foreground"> · {installments}× {brl(perInstallment)}</span>}
          </div>
          <button onClick={onClose} className="neu-tab h-9 px-3 rounded-lg text-sm text-muted-foreground hover:bg-secondary">Cancelar</button>
          <button onClick={save} disabled={saving}
            className="neu-relief inline-flex items-center gap-2 h-9 px-4 rounded-lg bg-primary text-primary-foreground text-sm font-medium disabled:opacity-50">
            {saving && <FiLoader className="w-4 h-4 animate-spin" />} Registrar venda
          </button>
        </>
      }
    >
      {err && <div className="text-sm text-red-500">{err}</div>}

      {/* PLANOS (21/10/2026): o formulário virou seções de papel (.pln-secao)
          sobre o chão da gaveta — Venda / Itens e desconto / Recebimento /
          Fiscal. Campos h-9, rótulos 13px; ordem e campos iguais. */}
      <section className="pln-secao">
      <h4 className="pln-secao-titulo">Venda</h4>
      {/* Tipo da venda (tabs) */}
      <div className="flex gap-1 p-1 rounded-lg bg-secondary/50 w-fit">
        {TYPE_TABS.map(t => (
          <button key={t.v} onClick={() => setSaleType(t.v)}
            className={`h-8 px-3 rounded-md text-sm font-medium transition-colors ${saleType === t.v ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}>
            {t.l}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-[13px] font-medium text-foreground mb-1.5">Número da venda*</label>
          <input value={saleNumber} onChange={e => setSaleNumber(e.target.value)} className={`${inputCls} h-9`} placeholder="Ex.: V-0001" />
        </div>
        <div>
          <label className="block text-[13px] font-medium text-foreground mb-1.5">Data da venda*</label>
          <input type="date" value={saleDate} onChange={e => setSaleDate(e.target.value)} className={`${inputCls} h-9`} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-[13px] font-medium text-foreground mb-1.5">Cliente*</label>
          <input value={client} onChange={e => setClient(e.target.value)} className={`${inputCls} h-9`} placeholder="Nome do cliente" />
        </div>
        <div>
          <label className="block text-[13px] font-medium text-foreground mb-1.5">CPF/CNPJ</label>
          <input value={clientDoc} onChange={e => setClientDoc(e.target.value)} className={`${inputCls} h-9`} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-[13px] font-medium text-foreground mb-1.5">Categoria de receita*</label>
          <select value={categoryId} onChange={e => setCategoryId(e.target.value)} className={`${inputCls} h-9`}>
            <option value="">Selecione…</option>
            {cats.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[13px] font-medium text-foreground mb-1.5">Centro de custo</label>
          <select value={costCenterId} onChange={e => setCostCenterId(e.target.value)} className={`${inputCls} h-9`}>
            <option value="">—</option>
            {ccs.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-[13px] font-medium text-foreground mb-1.5">Situação da negociação</label>
          <input value={negotiation} onChange={e => setNegotiation(e.target.value)} className={`${inputCls} h-9`} placeholder="Ex.: Fechada" />
        </div>
        <div>
          <label className="block text-[13px] font-medium text-foreground mb-1.5">Vendedor responsável</label>
          <input value={seller} onChange={e => setSeller(e.target.value)} className={`${inputCls} h-9`} />
        </div>
      </div>

      </section>

      {/* Itens */}
      <section className="pln-secao">
        <h4 className="pln-secao-titulo">Itens e desconto</h4>
        <label className="flex items-center gap-2 text-sm font-medium text-foreground cursor-pointer">
          <input type="checkbox" checked={detailItems} onChange={e => setDetailItems(e.target.checked)} className="neu-check rounded border-border" />
          Detalhar itens
        </label>
        {detailItems ? (
          <div className="space-y-2">
            {items.map((it, i) => (
              <div key={i} className="flex items-center gap-2">
                <input value={it.description} onChange={e => setItem(i, 'description', e.target.value)} className={`${inputCls} h-9 flex-1`} placeholder="Descrição do item" />
                <input type="number" step="0.001" min="0" value={it.quantity} onChange={e => setItem(i, 'quantity', e.target.value)} className={`${inputBase} h-9 w-20`} title="Quantidade" />
                <input type="number" step="0.01" min="0" value={it.unit_price} onChange={e => setItem(i, 'unit_price', e.target.value)} className={`${inputBase} h-9 w-28`} placeholder="Unit." title="Valor unitário" />
                <button onClick={() => removeItem(i)} disabled={items.length === 1} className="w-9 h-9 shrink-0 flex items-center justify-center rounded-md text-muted-foreground hover:text-red-500 hover:bg-red-500/10 disabled:opacity-30">
                  <FiTrash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
            <button onClick={addItem} className="inline-flex items-center gap-1.5 h-9 text-sm text-primary hover:underline"><FiPlus className="w-4 h-4" /> Adicionar item</button>
          </div>
        ) : (
          <div>
            <label className="block text-[13px] font-medium text-foreground mb-1.5">Valor total dos itens*</label>
            <input type="number" step="0.01" min="0" value={grossTotal} onChange={e => setGrossTotal(e.target.value)} className={`${inputCls} h-9`} placeholder="0,00" />
          </div>
        )}

      {/* Desconto + breakdown (sub-caixa leve dentro da seção) */}
      <div className="rounded-lg border border-border p-3 space-y-2.5">
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <label className="block text-[13px] font-medium text-foreground mb-1.5">Desconto</label>
            <input type="number" step="0.01" min="0" value={discountValue} onChange={e => setDiscountValue(e.target.value)} className={`${inputCls} h-9`} placeholder="0" />
          </div>
          <div className="flex gap-1 p-1 rounded-lg bg-secondary/50">
            {(['valor', 'percent'] as const).map(t => (
              <button key={t} onClick={() => setDiscountType(t)}
                className={`h-7 px-3 rounded-md text-sm font-medium ${discountType === t ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground'}`}>
                {t === 'valor' ? 'R$' : '%'}
              </button>
            ))}
          </div>
        </div>
        <div className="flex items-center justify-between text-sm pt-1 border-t border-border/60">
          <span className="text-muted-foreground">Itens {brl(itemsTotal)} − Desconto {brl(discountAmount)}</span>
          <span className="font-semibold text-foreground">= {brl(netTotal)}</span>
        </div>
      </div>
      </section>

      {/* Pagamento */}
      <section className="pln-secao">
      <h4 className="pln-secao-titulo">Recebimento</h4>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-[13px] font-medium text-foreground mb-1.5">Forma de recebimento</label>
          <select value={method} onChange={e => setMethod(e.target.value)} className={`${inputCls} h-9`}>
            <option value="">—</option>
            {METHODS.map(m => <option key={m.v} value={m.v}>{m.l}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[13px] font-medium text-foreground mb-1.5">Conta de recebimento</label>
          <select value={accountId} onChange={e => setAccountId(e.target.value)} className={`${inputCls} h-9`}>
            <option value="">—</option>
            {accs.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-[13px] font-medium text-foreground mb-1.5">Parcelas*</label>
          <input type="number" min="1" max="120" value={installments} onChange={e => setInstallments(Math.max(1, Number(e.target.value)))} className={`${inputCls} h-9`} />
        </div>
        <div>
          <label className="block text-[13px] font-medium text-foreground mb-1.5">Vencimento (1ª parcela)*</label>
          <input type="date" value={due} onChange={e => setDue(e.target.value)} className={`${inputCls} h-9`} />
        </div>
      </div>

      </section>

      {/* Informações fiscais / observações (colapsável). Seção de papel com o
          próprio cabeçalho clicável no lugar do título (15px, como pln-secao-titulo). */}
      <section className="pln-secao !p-0">
        <button onClick={() => setFiscalOpen(o => !o)} className="w-full flex items-center gap-2 px-4 py-3.5 text-[15px] font-semibold text-foreground">
          {fiscalOpen ? <FiChevronDown className="w-4 h-4" /> : <FiChevronRight className="w-4 h-4" />}
          Informações fiscais e observações
        </button>
        {fiscalOpen && (
          <div className="!mt-0 px-4 pb-4 space-y-3 border-t border-border pt-4">
            <div>
              <label className="block text-[13px] font-medium text-foreground mb-1.5">Natureza da operação</label>
              <input value={fiscalNature} onChange={e => setFiscalNature(e.target.value)} className={`${inputCls} h-9`} placeholder="Ex.: Venda de mercadoria" />
            </div>
            <div>
              <label className="block text-[13px] font-medium text-foreground mb-1.5">Observações</label>
              <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} className={inputCls} />
            </div>
          </div>
        )}
      </section>
    </SideModal>
  )
}
